import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe2, Eye, FileText, CalendarClock, MessageSquareHeart, CreditCard, Siren, ArrowRight } from "lucide-react";

const cities = ["Another Indian city", "Dubai / UAE", "USA", "UK", "Canada", "Australia", "Singapore", "Anywhere else"];

const remote = [
  { icon: Eye, title: "See their status in 5 seconds", text: "A simple daily overview: how your parents are doing, the last visit, and what's next." },
  { icon: FileText, title: "Read every visit report", text: "Check-in times, tasks completed, observations and follow-ups — written in plain language." },
  { icon: CalendarClock, title: "Track appointments & calendar", text: "Doctor visits, diagnostics, physiotherapy and ParentOne visits in one calendar." },
  { icon: MessageSquareHeart, title: "Request anything, anytime", text: "A Care Manager visit, hospital accompaniment, errands or a nurse — a few taps, any time zone." },
  { icon: Siren, title: "Get alerted in emergencies", text: "Escalation workflow reaches you instantly, wherever in the world you are." },
  { icon: CreditCard, title: "Manage billing remotely", text: "Subscription, invoices and receipts — visible and downloadable from abroad." },
];

export default function ForFamilies() {
  return (
    <div data-testid="for-families-page">
      <section className="p1-container py-14 lg:py-20">
        <div className="max-w-3xl">
          <p className="p1-eyebrow">For Families & NRIs</p>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
            You may be thousands of kilometres away. <span className="text-emerald-900">Your parents should never feel alone in managing life.</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-stone-600">
            Whether you're in Mumbai or Melbourne, ParentOne becomes your eyes, ears and hands at home — one dedicated team that knows your parents and answers to you.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {cities.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-600" data-testid={`location-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Globe2 className="h-3.5 w-3.5 text-emerald-800" /> {c}
              </span>
            ))}
          </div>
          <Link to="/onboarding" className="p1-btn-primary mt-8" data-testid="families-cta">Book a Free Assessment <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-white py-16">
        <div className="p1-container">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">What you can monitor and request — from anywhere</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {remote.map((r, i) => (
              <motion.div key={r.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="p1-card p-6" data-testid={`remote-feature-${i}`}>
                <r.icon className="h-7 w-7 text-emerald-800" />
                <h3 className="mt-3 font-heading text-lg font-semibold text-stone-900">{r.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{r.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="p1-container py-16">
        <div className="rounded-3xl bg-terracotta-soft/60 p-8 sm:p-12">
          <div className="max-w-2xl">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">Built for time zones and distance</h2>
              <ul className="mt-5 space-y-3 text-sm text-stone-700">
                <li>• Updates land in your ParentOne inbox and by email — readable in your morning, their evening. WhatsApp updates are being introduced.</li>
                <li>• One family account: siblings across the world share the same picture, with permissions you control.</li>
                <li>• Emergency escalation alerts the whole family in the app at the same time.</li>
                <li>• Pay from abroad — INR plans with receipts for your records. Online payment gateway is being introduced.</li>
              </ul>
              <Link to="/plans" className="p1-btn-primary mt-7" data-testid="families-plans-cta">See Plans & Pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
